import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot,
  CheckCircle, Plus, ArrowsLeftRight,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
// import WizardFields from "@/components/erp/WizardFields";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Transfer type constants — must match backend state.py
const TRANSFER_TYPE_LOCATION = "location";
const TRANSFER_TYPE_CATEGORY = "category";
const TRANSFER_TYPE_DEPEX    = "depex";

// Map agent state field names → WizardFields keys
const CHAT_TO_WIZARD = {
  transfer_type:        "transfer_type",
  book_type_code:       "book_type",
  asset_type:           "asset_type",
  category_major:       "asset_category",
  category_id:          "asset_category",
  category_name:        "asset_category",
  asset_number:         "asset_number",
  asset_description:    "description",
  // Location
  current_location:     "current_location",
  new_location_country: "new_location",
  new_location_ccid:    "new_location",
  new_location_name:    "new_location",
  // Category
  new_category_major:   "new_category",
  new_category_id:      "new_category",
  new_category_name:    "new_category",
  // DepEx
  current_depex_ccid:   "current_depex",
  current_depex_name:   "current_depex",
  new_depex_ccid:       "new_depex",
  new_depex_name:       "new_depex",
};

// ─────────────────────────────────────────────────────────────────────────────
// Markdown renderer
// ─────────────────────────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p:      ({ node, ...p }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-bold text-gray-900" {...p} />,
          ul:     ({ node, ...p }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...p} />,
          ol:     ({ node, ...p }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...p} />,
          li:     ({ node, ...p }) => <li className="text-sm" {...p} />,
          h3:     ({ node, ...p }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...p} />,
          code:   ({ node, inline, ...p }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...p} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...p} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline field UI widgets
// ─────────────────────────────────────────────────────────────────────────────

/**
 * AssetSearchWidget — shown after sub-category is selected.
 * Auto-fetched assets appear as a filterable table. Typing and pressing
 * Search triggers a new Oracle search by asset number or description.
 */
function AssetSearchWidget({ fieldUi, onPick, onSearch }) {
  const [q, setQ] = React.useState("");
  const allRows   = fieldUi.rows || [];

  const filtered = q
    ? allRows.filter((r) =>
        (r.cells || []).some((c) => String(c).toLowerCase().includes(q.toLowerCase()))
      )
    : allRows;

  const handleSearch = () => { if (q.trim()) onSearch?.(q.trim()); };

  const columns = fieldUi.columns || [];

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center border-b border-gray-100">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
          placeholder={fieldUi.placeholder || "Search by asset number or description…"}
          className="flex-1 px-3 py-2 text-xs outline-none bg-white"
        />
        <button
          onClick={handleSearch}
          className="px-3 py-2 text-xs text-[#0020C2] font-semibold border-l border-gray-100 hover:bg-[#0020C2]/5"
        >
          Search Oracle
        </button>
      </div>
      {columns.length > 0 && (
        <div
          className="grid text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100"
          style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
        >
          {columns.map((c) => <span key={c}>{c}</span>)}
        </div>
      )}
      <div className="max-h-56 overflow-y-auto">
        {allRows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No assets found. Type an asset number or description and click Search Oracle.
          </div>
        )}
        {allRows.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No matches for "{q}" in the loaded list —{" "}
            <button onClick={handleSearch} className="text-[#0020C2] underline">search Oracle</button>
          </div>
        )}
        {filtered.slice(0, 100).map((row, i) => (
          <div
            key={i}
            onClick={() => onPick({ label: row.label, value: row.value,
              asset_number: row.asset_number, asset_description: row.asset_description })}
            className="grid px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
            style={{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, 1fr)` }}
          >
            {(row.cells || [row.label]).map((cell, ci) => (
              <span key={ci} className="text-gray-700 truncate pr-2">{cell}</span>
            ))}
          </div>
        ))}
      </div>
      {filtered.length > 100 && (
        <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
          Showing 100 of {filtered.length} — type to filter or search Oracle
        </div>
      )}
    </div>
  );
}

function TableSelectOptions({ fieldUi, onPick }) {
  const rows    = fieldUi.rows    || [];
  const columns = fieldUi.columns || [];
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <div
        className="grid text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100"
        style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
      >
        {columns.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="max-h-52 overflow-y-auto">
        {rows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No items found.</div>
        )}
        {rows.map((row, i) => (
          <div
            key={i}
            onClick={() => onPick({
              label:             row.label,
              value:             row.value,
              category_id:       row.category_id,
              location_ccid:     row.location_ccid,
              asset_number:      row.asset_number,
              asset_description: row.asset_description,
            })}
            className="grid px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
            style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
          >
            {(row.cells || []).map((cell, ci) => (
              <span key={ci} className="text-gray-700 truncate pr-2">{cell}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * DepexAccountSelect — dedicated renderer for the DepEx account picker.
 * Two columns: CODE_COMBINATION_ID (narrow) | CONCATENATED_SEGMENTS (wide, no truncation).
 * The full account combination string must be visible — no text-overflow ellipsis.
 */
function DepexAccountSelect({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const allRows   = fieldUi.rows || [];

  const filtered = q
    ? allRows.filter((r) =>
        (r.cells || []).some((c) => String(c).toLowerCase().includes(q.toLowerCase()))
      )
    : allRows;

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      {/* Search box */}
      <div className="flex items-center border-b border-gray-100">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={fieldUi.placeholder || "Search by account combination or code…"}
          className="flex-1 px-3 py-2 text-xs outline-none bg-white"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="px-3 py-2 text-xs text-gray-400 hover:text-gray-600 border-l border-gray-100"
          >✕</button>
        )}
      </div>

      {/* Column headers: CODE_COMBINATION_ID narrow, CONCATENATED_SEGMENTS wide */}
      <div className="flex text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1.5 border-b border-gray-100 gap-2">
        <span className="shrink-0 w-28">CODE_COMBINATION_ID</span>
        <span className="flex-1">CONCATENATED_SEGMENTS</span>
      </div>

      {/* Rows — horizontally scrollable so long segment strings are never clipped */}
      <div className="max-h-56 overflow-y-auto">
        {allRows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No accounts found. Check backend logs for BIP fetch status.
          </div>
        )}
        {allRows.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No matches for "{q}" — try a different code or segment.
          </div>
        )}
        {filtered.map((row, i) => {
          const ccid    = (row.cells || [])[0] || row.value || "";
          const segment = (row.cells || [])[1] || row.label || "";
          return (
            <div
              key={i}
              onClick={() => onPick({ label: segment, value: row.value })}
              className="flex gap-2 px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0 items-start"
            >
              {/* CODE_COMBINATION_ID — narrow fixed width */}
              <span className="shrink-0 w-28 font-mono text-gray-600 pt-0.5">{ccid}</span>
              {/* CONCATENATED_SEGMENTS — full width, wraps naturally, never truncated */}
              <span className="flex-1 text-gray-800 break-all leading-snug">{segment}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options  = fieldUi.options || [];
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <div
      className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}
    >
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${(fieldUi.label || fieldUi.field).replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-44 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No options available. Check your Oracle connection.
          </div>
        )}
        {options.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches for "{q}"</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function RadioOptions({ fieldUi, onPick }) {
  const options = fieldUi.options || [];
  return (
    <div
      className="mt-2 flex flex-wrap gap-1.5"
      data-testid={`chat-field-ui-${fieldUi.field}`}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat bubble
// ─────────────────────────────────────────────────────────────────────────────

function TransferChatBubble({ msg, onOptionClick, onAssetSearch, isLastAssistant }) {
  const isUser  = msg.role === "user";
  const fieldUi = !isUser && !msg.loading && isLastAssistant
    ? msg.result?.next_field_ui
    : null;
  const uiType  = fieldUi?.uiType;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div
        className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white
          ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}
      >
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      <div
        className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
          ${isUser
            ? "bg-[#0020C2] text-white rounded-tr-sm"
            : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"
          }`}
      >
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span
                key={d}
                className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }}
              />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "radio" && (
          <RadioOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "table_select" && (
          <TableSelectOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "depex_account_select" && (
          <DepexAccountSelect fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {uiType === "asset_search" && (
          <AssetSearchWidget
            fieldUi={fieldUi}
            onPick={(opt) => onOptionClick?.(opt)}
            onSearch={(query) => onAssetSearch?.(query)}
          />
        )}
        {fieldUi &&
          !["searchable_dropdown", "radio", "table_select", "depex_account_select"].includes(uiType) &&
          fieldUi.options?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`chat-field-ui-${fieldUi.field}`}>
              {fieldUi.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onOptionClick?.(opt)}
                  className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress bar
// ─────────────────────────────────────────────────────────────────────────────

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch  = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#0020C2] transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Input bar
// ─────────────────────────────────────────────────────────────────────────────

function TransferInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div
        className="flex items-end gap-2 border border-gray-300 bg-gray-50
          focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100"
      >
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder='Describe the asset or type a number — e.g. "Transfer the diesel generator"'
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none
            placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0"
        >
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Success state
// ─────────────────────────────────────────────────────────────────────────────

function SuccessBanner({ reply, onClose }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 bg-gray-50">
      <div className="w-16 h-16 rounded-full bg-[#0020C2]/10 flex items-center justify-center mb-5">
        <CheckCircle size={36} weight="fill" className="text-[#0020C2]" />
      </div>
      <h2 className="text-xl font-black text-gray-900 mb-2">Asset Transferred!</h2>
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <ChatMarkdown>{reply}</ChatMarkdown>
      </div>
      <button
        onClick={onClose}
        className="mt-6 px-6 py-2 rounded-lg text-sm font-bold text-white bg-[#0020C2] hover:bg-[#001AA3] transition-colors"
      >
        Close
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wizard panel — summary of transfer fields
// ─────────────────────────────────────────────────────────────────────────────

function TransferWizardPanel({ fieldValues }) {
  const transferType = fieldValues.transfer_type;

  // Base fields shown for all transfer types
  const baseFields = [
    { key: "book_type",      label: "Asset Book" },
    { key: "asset_type",     label: "Asset Type" },
    { key: "asset_category", label: "Category" },
    { key: "asset_number",   label: "Asset Number" },
    { key: "description",    label: "Description" },
  ];

  // Type-specific fields appended after the base fields
  const typeFields =
    transferType === TRANSFER_TYPE_CATEGORY
      ? [
          { key: "asset_category", label: "Current Category" },
          { key: "new_category",   label: "New Category" },
        ]
      : transferType === TRANSFER_TYPE_DEPEX
      ? [
          { key: "current_depex", label: "Current Deprn Exp Account" },
          { key: "new_depex",     label: "New Deprn Exp Account" },
        ]
      : [
          // Location (default)
          { key: "current_location", label: "Current Location" },
          { key: "new_location",     label: "New Location" },
        ];

  // Deduplicate (base already shows category; type fields show current/new)
  const shownBaseFields = baseFields.filter(
    (f) => !typeFields.some((t) => t.key === f.key)
  );
  const fields = [...shownBaseFields, ...typeFields];

  // Transfer type badge
  const typeLabel = {
    [TRANSFER_TYPE_LOCATION]: "📍 Location → Location",
    [TRANSFER_TYPE_CATEGORY]: "🗂️ Category → Category",
    [TRANSFER_TYPE_DEPEX]:    "💰 DepEx Account → Account",
  }[transferType] || null;

  return (
    <div className="space-y-3">
      {/* Transfer type badge */}
      {typeLabel && (
        <div className="text-[10px] font-bold px-2 py-1 rounded bg-[#0020C2]/10 text-[#0020C2] inline-block">
          {typeLabel}
        </div>
      )}
      {fields.map(({ key, label }) => {
        const val = fieldValues[key];
        return (
          <div key={key} className="space-y-0.5">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              {label}
            </div>
            <div
              className={`text-sm px-3 py-2 rounded border ${
                val
                  ? "border-[#0020C2]/20 bg-[#0020C2]/5 text-gray-900 font-medium"
                  : "border-gray-100 bg-gray-50 text-gray-400 italic"
              }`}
            >
              {val || "Not yet provided"}
            </div>
          </div>
        );
      })}

      {/* Transfer direction visual — type-aware */}
      {transferType === TRANSFER_TYPE_LOCATION &&
        fieldValues.current_location && fieldValues.new_location && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="text-[10px] font-bold uppercase text-amber-700 mb-2 flex items-center gap-1">
            <ArrowsLeftRight size={11} /> Location Transfer
          </div>
          <div className="text-xs text-gray-700 space-y-1">
            <div><span className="text-gray-500">From:</span> {fieldValues.current_location}</div>
            <div className="text-center text-amber-400">↓</div>
            <div><span className="text-gray-500">To:</span> {fieldValues.new_location}</div>
          </div>
        </div>
      )}
      {transferType === TRANSFER_TYPE_CATEGORY &&
        fieldValues.new_category && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-[10px] font-bold uppercase text-blue-700 mb-2 flex items-center gap-1">
            <ArrowsLeftRight size={11} /> Category Transfer
          </div>
          <div className="text-xs text-gray-700 space-y-1">
            <div><span className="text-gray-500">From:</span> {fieldValues.asset_category || "—"}</div>
            <div className="text-center text-blue-400">↓</div>
            <div><span className="text-gray-500">To:</span> {fieldValues.new_category}</div>
          </div>
        </div>
      )}
      {transferType === TRANSFER_TYPE_DEPEX &&
        fieldValues.new_depex && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="text-[10px] font-bold uppercase text-green-700 mb-2 flex items-center gap-1">
            <ArrowsLeftRight size={11} /> DepEx Account Transfer
          </div>
          <div className="text-xs text-gray-700 space-y-1">
            <div><span className="text-gray-500">From:</span> {fieldValues.current_depex || "—"}</div>
            <div className="text-center text-green-400">↓</div>
            <div><span className="text-gray-500">To:</span> {fieldValues.new_depex}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main modal
// ─────────────────────────────────────────────────────────────────────────────

export default function AssetTransferAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  const agent = agents?.find((a) => a.key === openAgent) || {
    label: "Asset Transfer",
    hint:  "Transfer a fixed asset by Location, Category, or Depreciation Expense Account",
  };

  const SESSION_KEY = "asset_transfer_session_id";

  const [sessionId,       setSessionId]       = React.useState(() => sessionStorage.getItem(SESSION_KEY) || "");
  const [messages,        setMessages]         = React.useState([]);
  const [input,           setInput]            = React.useState("");
  const [busy,            setBusy]             = React.useState(false);
  const [progress,        setProgress]         = React.useState(null);
  const [transferDone,    setTransferDone]     = React.useState(false);
  const [successReply,    setSuccessReply]     = React.useState("");
  const [started,         setStarted]          = React.useState(false);
  const [fieldValues,     setFieldValues]      = React.useState({});
  const [chatStarted,     setChatStarted]      = React.useState(false);

  const messagesEndRef = React.useRef(null);
  const inputRef       = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Initialize session ────────────────────────────────────────────────────
  React.useEffect(() => {
    if (started) return;
    setStarted(true);

    const doInit = async () => {
      setBusy(true);
      const existingSid = sessionStorage.getItem(SESSION_KEY);

      if (existingSid) {
        setSessionId(existingSid);
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
        return;
      }

      try {
        const { data } = await api.post("/erp-agents/asset-transfer/start");
        const sid = data.session_id;
        sessionStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
      } catch (e) {
        console.warn("[AssetTransfer] /start failed:", e?.response?.data?.detail || e.message);
      } finally {
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 150);
      }
    };

    doInit();
  }, [started]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync chat fields_collected → fieldValues ──────────────────────────────
  const syncFieldsToWizard = React.useCallback((fieldsCollected) => {
    if (!fieldsCollected || typeof fieldsCollected !== "object") return;
    setFieldValues((prev) => {
      const merged = { ...prev };

      // Transfer type — always sync
      if (fieldsCollected.transfer_type) merged["transfer_type"] = String(fieldsCollected.transfer_type);

      // Human-readable names take priority over IDs/CCIDs
      if (fieldsCollected.category_name)     merged["asset_category"] = String(fieldsCollected.category_name);
      if (fieldsCollected.asset_description) merged["description"]    = String(fieldsCollected.asset_description);
      if (fieldsCollected.current_location)  merged["current_location"] = String(fieldsCollected.current_location);

      // Location transfer
      if (fieldsCollected.new_location_name) merged["new_location"] = String(fieldsCollected.new_location_name);

      // Category transfer
      if (fieldsCollected.new_category_name) merged["new_category"] = String(fieldsCollected.new_category_name);
      else if (fieldsCollected.new_category_id && !merged["new_category"])
        merged["new_category"] = String(fieldsCollected.new_category_id);

      // DepEx transfer — display_name first, fall back to CCID
      if (fieldsCollected.current_depex_name) merged["current_depex"] = String(fieldsCollected.current_depex_name);
      else if (fieldsCollected.current_depex_ccid && !merged["current_depex"])
        merged["current_depex"] = String(fieldsCollected.current_depex_ccid);
      if (fieldsCollected.new_depex_name)     merged["new_depex"] = String(fieldsCollected.new_depex_name);
      else if (fieldsCollected.new_depex_ccid && !merged["new_depex"])
        merged["new_depex"] = String(fieldsCollected.new_depex_ccid);

      // Generic pass for remaining fields (skip fields already handled above)
      const skipKeys = new Set([
        "transfer_type",
        "category_id", "category_name",
        "new_location_ccid", "new_location_name",
        "new_category_id", "new_category_name",
        "current_depex_ccid", "current_depex_name",
        "new_depex_ccid", "new_depex_name",
        "asset_description", "current_location",
      ]);
      for (const [chatKey, val] of Object.entries(fieldsCollected)) {
        if (val === null || val === undefined || val === "") continue;
        if (skipKeys.has(chatKey)) continue;
        const wizardKey = CHAT_TO_WIZARD[chatKey];
        if (!wizardKey) continue;
        if (merged[wizardKey] !== undefined && merged[wizardKey] !== "") continue;
        merged[wizardKey] = String(val);
      }

      return merged;
    });
  }, []);

  // ── Wizard changes → agent session ───────────────────────────────────────
  const handleWizardChange = React.useCallback(async (newValues) => {
    setFieldValues(newValues);

    const WIZARD_TO_AGENT = {
      book_type:      "book_type_code",
      asset_type:     "asset_type",
      asset_category: "category_name",
      asset_number:   "asset_number",
      new_location:   "new_location_name",
    };

    const agentFields = {};
    for (const [wizKey, agentKey] of Object.entries(WIZARD_TO_AGENT)) {
      const val = newValues[wizKey];
      if (val !== undefined && val !== null && String(val).trim() !== "") {
        agentFields[agentKey] = String(val);
      }
    }

    if (Object.keys(agentFields).length > 0 && sessionId) {
      try {
        await api.post("/erp-agents/asset-transfer/set-fields", {
          session_id: sessionId,
          fields:     agentFields,
        });
      } catch (e) {
        console.warn("[AssetTransfer] set-fields sync failed:", e?.response?.data?.detail || e.message);
      }
    }
  }, [sessionId]);

  // ── Send a user message ───────────────────────────────────────────────────
  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(),     role: "user",      text: userText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const { data } = await api.post("/erp-agents/asset-transfer/chat", {
        session_id: sessionId,
        message:    userText,
      });

      if (data.progress) setProgress(data.progress);
      if (data.fields_collected) syncFieldsToWizard(data.fields_collected);

      if (data.transfer_completed) {
        setTransferDone(true);
        setSuccessReply(data.reply || "Asset transferred successfully!");
      }

      const reply = data.reply || data.response || data.message || "Done.";
      const now2  = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now2 }
            : m
        )
      );
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e.message || "An error occurred.";
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `❌ ${errMsg}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  // ── New chat ──────────────────────────────────────────────────────────────
  const startNewChat = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setInput("");
    setProgress(null);
    setTransferDone(false);
    setSuccessReply("");
    setStarted(false);
    setChatStarted(false);
    setFieldValues({});
  };

  const close = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setOpenAgent(null);
    onClose?.();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes transferChatFadeIn  { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes transferWizardSlide { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        @keyframes transferFadeIn      { from { opacity:0; transform:translateY(8px);   } to { opacity:1; transform:translateY(0); } }
        .transfer-modal-pre  { width: 640px; max-width: calc(100vw - 2rem); max-height: 90vh; animation: transferFadeIn 0.3s ease; }
        .transfer-modal-chat { width: min(92vw, 1400px); height: 90vh; animation: transferFadeIn 0.3s ease; }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={close}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden shadow-2xl
            ${chatStarted ? "transfer-modal-chat" : "transfer-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* ── Header ── */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5 flex items-center gap-2">
                <ArrowsLeftRight size={22} className="text-[#0020C2]" />
                {agent.label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <button
                    onClick={startNewChat}
                    className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
                    title="Start a new conversation"
                  >
                    <Plus size={13} weight="bold" /> New Chat
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen?.(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
              >
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close" className="text-gray-400 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          {transferDone ? (
            <SuccessBanner reply={successReply} onClose={close} />

          ) : !chatStarted ? (

            /* ── Pre-chat: wizard summary + input ── */
            <>
              <div className="flex-1 overflow-y-auto p-5">
                <p className="text-sm text-gray-600 mb-4">
                  Transfer a fixed asset by <strong>Location</strong>, <strong>Category</strong>, or <strong>Depreciation Expense Account</strong>.
                  Select the transfer type when prompted, then follow the guided workflow.
                </p>
                <TransferWizardPanel fieldValues={fieldValues} />
              </div>
              <TransferInputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
              />
            </>

          ) : (

            /* ── Chat mode: 60% chat / 40% wizard ── */
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT — chat panel */}
              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "transferChatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <ProgressBar progress={progress} />

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => {
                    const lastAssistantId = [...messages]
                      .reverse()
                      .find((m) => m.role === "assistant" && !m.loading)?.id;
                    const isLast = msg.role === "assistant" && msg.id === lastAssistantId;
                    return (
                      <TransferChatBubble
                        key={msg.id}
                        msg={msg}
                        isLastAssistant={isLast}
                        onOptionClick={(opt) => {
                          const fui = [...messages].reverse()
                            .find((m) => m.role === "assistant" && !m.loading)
                            ?.result?.next_field_ui;
                          const field = fui?.field;

                          // Immediately update wizard panel for row-click selections
                          if (field === "new_location_ccid" || opt.location_ccid) {
                            const displayName = opt.label || opt.display_name || opt.value;
                            setFieldValues((prev) => ({ ...prev, new_location: displayName }));
                          } else if (field === "new_category_id") {
                            // opt.label = full_name when category row clicked
                            const displayName = opt.label || opt.value;
                            setFieldValues((prev) => ({ ...prev, new_category: displayName }));
                          } else if (field === "new_depex_ccid") {
                            const displayName = opt.label || opt.display_name || opt.value;
                            setFieldValues((prev) => ({ ...prev, new_depex: displayName }));
                          } else if (field === "transfer_type") {
                            // Show the transfer type badge immediately
                            const label = opt.value;
                            setFieldValues((prev) => ({ ...prev, transfer_type: label }));
                          }
                          sendMessage(opt.value);
                        }}
                        onAssetSearch={(query) => sendMessage(query)}
                      />
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <TransferInputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                />
              </div>

              {/* RIGHT — wizard / summary panel */}
              <div
                className="flex flex-col bg-white"
                style={{ width: "40%", minWidth: "360px", animation: "transferWizardSlide 0.45s cubic-bezier(0.4,0,0.2,1)" }}
              >
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Transfer Details
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      · auto-filled as you chat
                    </span>
                  </div>
                  <TransferWizardPanel fieldValues={fieldValues} />
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}